<docs>
  # HeaderThematique
  > The Header for thematique page
  
  @author Kffein <contact@kffein.com>
</docs>

<script lang="ts">
import { templateRef, tryOnMounted, tryOnUnmounted } from "@vueuse/core";
import BureauSvg from "assets/svg/new-bureau.svg?raw";
import ThematiqueSvg from "assets/svg/thematique.svg?raw";
import { store as NavStore } from 'components/ui/Header/store';
import { store as themeStore } from "pages/Thematique/store";
import emitter, { EVENTS } from "plugins/store/events";
import { store as appStore } from "store/app";
import { stopSound } from "utils/helpers/Sound";
import { gsap } from "vendors/gsap";
import { computed, defineComponent, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from "vue-router";

export default defineComponent({
  name: "HeaderThematique",
  setup() {

    const route = useRoute();

    const { t } = useI18n();
    const el = templateRef<HTMLElement>("el");

    const currentVariant = computed(() => themeStore.current);
    const isIntro = computed(() => themeStore.isIntro);
    const showThemeHeader = computed(() => themeStore.showThemeHeader);
    const disabled = computed(() => appStore.disabled);
    const isThemeRoute = computed(() => route.name === 'thematique')

    // Check when a theme is active and we want to see the themeNav => so show the header
    watch(
      showThemeHeader,
      (bool) => {
        if (bool) {
          if (el.value.classList.contains("hidden")) animateIn();
        }
      }
    )

    // Tween to display elements 
    const animateIn = () => {
      gsap.timeline()
        .add(() => {
          el.value.classList.remove("hidden");
        }, 0)
        .from(el.value, { yPercent: -150, ease: "power4.out", duration: 0.75 }, 0.2)
    }

    // Tween to hide elements 
    const animateOut = () => {
      el.value.classList.add("hidden");
    }

    const onBackHome = (e: PointerEvent) => {
      e.preventDefault();
      animateOut();

      appStore.setPageTransition({ name: 'home' });
      themeStore.toggleThemeHeader(false);
      themeStore.toggleFromListing(false);

      stopSound(`${themeStore.current?.id}-track`);
    }

    tryOnMounted(() => {
      emitter.on(EVENTS.CLICK_OBJECT, animateOut);
    })

    tryOnUnmounted(() => {
      emitter.off(EVENTS.CLICK_OBJECT, animateOut);
      themeStore.toggleIntro(false);
    })
    // Toggle Nav
    const toggleNav = () => {
      NavStore.openState(!NavStore.isOpen);
    }

    return {
      t,
      currentVariant,
      isThemeRoute,
      disabled,
      isIntro,
      BureauSvg,
      ThematiqueSvg,
      onBackHome,
      gradientColor1: computed(() => `rgba(${appStore.bgColor}, 0)`),
      gradientColor2: computed(() => `rgba(${appStore.bgColor}, .79)`),
      gradientColor3: computed(() => `rgba(${appStore.bgColor}, 1)`),
      toggleNav,
      isOpen: computed(() => NavStore.isOpen),
    };
  },
});
</script>

<template>
  <header v-if="isThemeRoute"
          ref="el"
          class="headerThematique hidden"
          :data-intro="isIntro"
          :data-disabled="disabled"
  >
    <ui-button :key="`header-thematique-${isIntro}`"
               :label="t('LABELS.home')"
               :theme="isIntro ? 'icon' : 'primary'"
               class="home"
               :to="{ name: 'home' }"
               :icon="BureauSvg"
               tag="a"
               :variant="currentVariant?.id"
               @click="onBackHome($event)"
    />
    <h4 class="title"
        :data-variant="currentVariant?.id"
        :data-dark="false"
        v-html="currentVariant?.title"
    />
    <ui-button class="link"
               :label="t('LABELS.themes')"
               :theme="isIntro ? 'icon' : 'primary'"
               :icon="ThematiqueSvg"
               :variant="currentVariant?.id"
               :expand="true"
               :selected="isOpen"
               @click="toggleNav"
    />
  </header>
</template>

<style lang="stylus" scoped>
  .headerThematique
    fixed top $gutter left $gutter
    f(center, $align:flex-start)
    width "calc(100% - ( 2* %s ))" % $gutter
    z-index $z-header
    opacity 1
    trans(opacity)
    user-select none
    
    &:after
      position absolute
      content ''
      display block
      top 'calc(%s * -1)' % ($gutter)
      left 'calc(%s * -1)' % ($gutter)
      width 100vw
      z-index -1
      rp(height, 90px)
      trans(height)
      trans(background, $duration:1.5s)
      pointer-events none
      background 'linear-gradient(0deg, %s 0%, %s 49%, %s 100%)' % (v-bind(gradientColor1) v-bind(gradientColor2) v-bind(gradientColor3))
    &[data-intro="true"]
      opacity 1
    &[data-disabled="true"]
      pointer-events none

    &.hidden
      opacity 0
      pointer-events none

    &.-blurry
      trans(opacity, $duration:1s)
      transition-delay 1s
      opacity 0 !important
    

  .home
    position absolute
    left 0px

  .title
    text-align center
    f-style("text16_thematique")
    addVariantProps($dark:false)
    margin 0 auto
    trans(transform, $duration:1.5s)
    :deep()
      .italic
        font-family $ff-title_italic
      .upper
        text-transform uppercase
    [data-intro="false"] &
      pointer-events none
      opacity 0
      user-select none
      transform translateY(-120%)
  </style>
